import { LoadingScreen } from "components/LoadingScreen";
import { useEffect, useState } from "react";
import { useGerenciarPagamentos } from "../../../hooks/useGerenciarPagamentos";
import { IFiltrosGerenciarPagamento } from "../../../interface/IFiltrosGerenciarPagamento";
import { FiltrosAguardando } from "./containers/FiltrosAguardando";
import { ListarAguardando } from "./containers/ListarAguardando";
import { ModalCertezaAprovarLote } from "./containers/ModalCertezaAprovarLote";
import { ModalCertezaReprovarLote } from "./containers/ModalCertezaReprovarLote";
import { ModalDetalhesLote } from "./containers/ModalDetalhesLote";
import { ModalSucessoAprovarLote } from "./containers/ModalSucessoAprovarLote";
import { ModalSucessoReprovarLote } from "./containers/ModalSucessoReprovarLote";
import { ContainerFiltros } from "./style";
import { toastErro } from "components/Toast";
import {
  PERMISSOES_GERENCIAR_PAGAMENTOS,
  temPermissao,
} from "../../../constants/permissoes";

interface IProps {
  aba: string;
  onAtualizarContadores: () => void;
  onCriarLote: () => void;
  listaPermissoes: { nome_funcionalidade: string }[];
}

export function TabAguardandoAprovacao({
  aba,
  onAtualizarContadores,
  onCriarLote,
  listaPermissoes,
}: IProps) {
  const {
    dados,
    totalRows,
    limit,
    page,
    loading,
    filtro,
    setLimit,
    setPage,
    listar,
    selectedRows,
    setSelectedRows,
    clearRows,
    idLoteEscolhido,
    setIdLoteEscolhido,
    dadosDetalhes,
    setDadosDetalhes,
    buscarDetalhesLote,
    aprovarLotes,
    reprovarLotes,
    onClickProcesso,
    baixarReciboNf,
    pagamentoUrgenteAtivo,
    togglePagamentoUrgente,
    ordenacao,
    ordenar,
  } = useGerenciarPagamentos();

  const podeCriar = temPermissao(listaPermissoes, PERMISSOES_GERENCIAR_PAGAMENTOS.CRIAR);
  const podeAprovar = temPermissao(listaPermissoes, PERMISSOES_GERENCIAR_PAGAMENTOS.APROVAR);
  const podeReprovar = temPermissao(listaPermissoes, PERMISSOES_GERENCIAR_PAGAMENTOS.REPROVAR);

  const [showModalAprovar, setShowModalAprovar] = useState(false);
  const [showModalReprovar, setShowModalReprovar] = useState(false);
  const [showModalDetalhes, setShowModalDetalhes] = useState(false);
  const [showModalSucesso, setShowModalSucesso] = useState(false);
  const [showModalSucessoReprovar, setShowModalSucessoReprovar] = useState(false);

  useEffect(() => {
    if (aba !== "aguardando") return;
    listar();
  }, [aba, limit, page, filtro, ordenacao]);

  useEffect(() => {
    if (!idLoteEscolhido) return;
    buscarDetalhesLote(idLoteEscolhido)
      .then(({ hasErro }) => {
        if (!hasErro) setShowModalDetalhes(true);
      })
      .catch(() => toastErro("Erro ao buscar detalhes do lote"));
  }, [idLoteEscolhido]);

  const submitFiltro = (dados: IFiltrosGerenciarPagamento) => {
    listar(dados);
  };

  const handlePageChange = (p: number) => setPage(p);
  const handleLimitChange = (l: number) => setLimit(l);

  const onClickModalAprovar = () => {
    if (!selectedRows.length) {
      toastErro("É necessário selecionar um lote ou mais.");
      return;
    }
    setShowModalAprovar(true);
  };

  const onClickModalReprovar = () => {
    if (!selectedRows.length) {
      toastErro("É necessário selecionar um lote ou mais.");
      return;
    }
    setShowModalReprovar(true);
  };

  const onConfirmAprovar = async () => {
    setShowModalAprovar(false);
    setSelectedRows([]);
    const { hasErro } = await aprovarLotes();
    if (!hasErro) {
      setShowModalSucesso(true);
      listar();
      onAtualizarContadores();
    }
  };

  const onConfirmReprovar = async (motivo: string) => {
    setShowModalReprovar(false);
    setSelectedRows([]);
    const { hasErro } = await reprovarLotes(motivo);
    if (!hasErro) {
      setShowModalSucessoReprovar(true);
      listar();
      onAtualizarContadores();
    }
  };

  const onClickVerDetalhes = (idLote: number) => {
    setIdLoteEscolhido(idLote);
  };

  const handleCloseModalDetalhes = () => {
    setShowModalDetalhes(false);
    setDadosDetalhes(null);
    setIdLoteEscolhido(null);
  };

  return (
    <ContainerFiltros>
      <LoadingScreen mostrar={loading} />
      <FiltrosAguardando submitForm={submitFiltro} />
      <ListarAguardando
        dados={dados}
        handleLimitChange={handleLimitChange}
        handlePageChange={handlePageChange}
        totalRows={totalRows}
        setRowsSelected={setSelectedRows}
        selectedRows={selectedRows}
        linhasPorPagina={limit}
        onClickAprovar={onClickModalAprovar}
        onClickReprovar={onClickModalReprovar}
        onClickVerDetalhes={onClickVerDetalhes}
        clearRows={clearRows}
        pagamentoUrgenteAtivo={pagamentoUrgenteAtivo}
        onTogglePagamentoUrgente={togglePagamentoUrgente}
        onBaixarRecibo={baixarReciboNf}
        onCriarLote={onCriarLote}
        onOrdenar={ordenar}
        podeCriar={podeCriar}
        podeAprovar={podeAprovar}
        podeReprovar={podeReprovar}
      />
      <ModalCertezaAprovarLote
        show={showModalAprovar}
        handleClose={() => setShowModalAprovar(false)}
        onConfirm={onConfirmAprovar}
      />
      <ModalCertezaReprovarLote
        show={showModalReprovar}
        handleClose={() => {
          setShowModalReprovar(false);
          setSelectedRows([]);
        }}
        onConfirm={onConfirmReprovar}
      />
      <ModalDetalhesLote
        show={showModalDetalhes}
        dados={dadosDetalhes}
        handleClose={handleCloseModalDetalhes}
        onClickProcesso={onClickProcesso}
        onBaixarRecibo={(idLote) => baixarReciboNf(idLote, dadosDetalhes?.numeroNfRecibo ?? String(idLote))}
      />
      <ModalSucessoAprovarLote
        show={showModalSucesso}
        handleClose={() => setShowModalSucesso(false)}
      />
      <ModalSucessoReprovarLote
        show={showModalSucessoReprovar}
        handleClose={() => setShowModalSucessoReprovar(false)}
      />
    </ContainerFiltros>
  );
}
